/**
 * ProfileEarnings.jsx — persona-aware:
 *   • Writer / Admin   → existing EarningsAndPayout panel (real payout flow)
 *   • Reader (`user`)  → Reading Rewards summary (coins · streak · gems)
 *
 * Both surfaces live under the same `/profile/earnings` route so the
 * Profile button grid can show "Earnings & Payout" for every persona
 * (Feb 26 2026 user spec — "all these individually").
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Coins, Flame, Gem, ShoppingBag } from 'lucide-react';
import ProfileSubPage from '../components/ProfileSubPage';
import EarningsAndPayout from '../components/EarningsAndPayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { checkAuth, authFetch } from '../utils/authUtils';
import { getActiveSpace, onSpaceChange, effectiveRoleFor } from '../utils/spaceContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProfileEarnings = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [rewards, setRewards] = useState({ coins: null, streak: null, gems: null });
  const [tick, setTick] = useState(0);

  useEffect(() => onSpaceChange(() => setTick((t) => t + 1)), []);

  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      setUser(u);
      // Coins intentionally follow the same authoritative bank summary used
      // by /bank. Streak comes from gamification/me; gems are exposed by the
      // Shop wallet contract. Keep unavailable fields null so the UI never
      // invents a zero when any request fails.
      const [bankResult, gamificationResult, shopResult] = await Promise.allSettled([
        authFetch(`${BACKEND_URL}/api/bank/me`),
        authFetch(`${BACKEND_URL}/api/gamification/me`),
        authFetch(`${BACKEND_URL}/api/shop/catalogue`),
      ]);

      const readJson = async (result) => {
        if (result.status !== 'fulfilled' || !result.value.ok) return null;
        try { return await result.value.json(); } catch (_) { return null; }
      };

      const [bank, gamification, shop] = await Promise.all([
        readJson(bankResult),
        readJson(gamificationResult),
        readJson(shopResult),
      ]);
      setRewards({
        coins: Number.isFinite(bank?.balance) ? bank.balance : null,
        streak: Number.isFinite(gamification?.streak_current) ? gamification.streak_current : null,
        gems: Number.isFinite(shop?.wallet?.gems) ? shop.wallet.gems : null,
      });
    })();
  }, [navigate]);

  if (!user) return null;

  const _tick = tick; // eslint-disable-line no-unused-vars
  const effective = effectiveRoleFor(user.role, getActiveSpace());
  const isWriterView = effective === 'writer' || effective === 'admin';
  const displayMetric = (value) => (Number.isFinite(value) ? value : '—');

  return (
    <ProfileSubPage title="Earnings & Payout" icon={Coins} accent="emerald">
      {isWriterView ? (
        <EarningsAndPayout />
      ) : (
        <>
          <Card className="bg-slate-800/50 border-slate-700" data-testid="reader-rewards-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Coins className="w-5 h-5 text-amber-400" /> Reading Rewards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-gradient-to-br from-amber-500/15 to-orange-500/10 border border-amber-400/30 p-4 text-center">
                  <Coins className="w-5 h-5 mx-auto text-amber-300 mb-1" />
                  <p className="text-2xl font-black text-amber-200 tabular-nums">{displayMetric(rewards.coins)}</p>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80 mt-1">Coins</p>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-rose-500/15 to-pink-500/10 border border-rose-400/30 p-4 text-center">
                  <Flame className="w-5 h-5 mx-auto text-rose-300 mb-1" />
                  <p className="text-2xl font-black text-rose-200 tabular-nums">{displayMetric(rewards.streak)}</p>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-rose-300/80 mt-1">Day Streak</p>
                </div>
                <div className="rounded-xl bg-gradient-to-br from-fuchsia-500/15 to-purple-500/10 border border-fuchsia-400/30 p-4 text-center">
                  <Gem className="w-5 h-5 mx-auto text-fuchsia-300 mb-1" />
                  <p className="text-2xl font-black text-fuchsia-200 tabular-nums">{displayMetric(rewards.gems)}</p>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-300/80 mt-1">Gems</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                Coins are earned by reading, finishing books, completing daily quests, and keeping
                your streak alive. Spend them in the Coin Shop on themes, boosts, and unlocks.
              </p>
            </CardContent>
          </Card>
          <Button
            onClick={() => navigate('/shop')}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
            data-testid="reader-rewards-open-shop"
          >
            <ShoppingBag className="w-4 h-4 mr-2" /> Open Coin Shop
          </Button>
        </>
      )}
    </ProfileSubPage>
  );
};

export default ProfileEarnings;
